import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Select, Textarea } from '../../components/ui/FormField.js';
import { Modal } from '../../components/ui/Modal.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

interface LocationItem {
  _id: string;
  name: string;
}

/**
 * 330 (US3, FR-010, contracts/ui.md) — staff change or clear a customer's campus, with a reason.
 *
 * The customer chose it once and cannot change it themselves; this is the way out for a transfer or a
 * wrong pick. "Not chosen" clears it, so the customer chooses again on their next visit. The reason is
 * required (3 characters or more) and lands in the customer's timeline with who, from and to.
 */
export function ChangeCampusModal({
  userId,
  currentCampus,
  onClose,
}: {
  userId: string;
  currentCampus: { _id: string; name: string; is_active: boolean } | null;
  onClose: () => void;
}) {
  const currentCampusId = currentCampus?._id ?? null;
  const queryClient = useQueryClient();
  const [campusId, setCampusId] = useState<string>(currentCampusId ?? '');
  const [reason, setReason] = useState('');
  const campusFieldId = useId();
  const reasonFieldId = useId();

  const { data: locations } = useQuery({
    queryKey: ['locations', 'campus-choices'],
    queryFn: () => api.get<{ items: LocationItem[] }>('/locations?limit=100'),
  });

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/users/${userId}/campus`, {
        location_id: campusId === '' ? null : campusId,
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.user.one(userId) }),
        queryClient.invalidateQueries({ queryKey: ['user-events', userId] }),
      ]);
      onClose();
    },
  });

  const changed = campusId !== (currentCampusId ?? '');
  const canSave = changed && reason.trim().length >= 3 && !save.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title="Change campus"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!canSave}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label htmlFor={campusFieldId} className="text-sm font-medium text-ink">
            Campus
          </label>
          <Select id={campusFieldId} value={campusId} onChange={(e) => setCampusId(e.target.value)}>
            {(locations?.items ?? []).map((l) => (
              <option key={l._id} value={l._id}>
                {l.name}
              </option>
            ))}
            {/* A deactivated current campus is not in the active list; show it, so the select says
                what the customer has today rather than silently showing another campus. */}
            {currentCampus &&
              !(locations?.items ?? []).some((l) => l._id === currentCampus._id) && (
                <option value={currentCampus._id}>{currentCampus.name} (inactive)</option>
              )}
            <option value="">Not chosen (the customer chooses again)</option>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={reasonFieldId} className="text-sm font-medium text-ink">
            Why is the campus changing?
          </label>
          <Textarea
            id={reasonFieldId}
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        {save.isError && (
          <p role="alert" className="text-sm text-semantic-danger-text">
            {(save.error as { message?: string }).message ?? 'Could not change the campus.'}
          </p>
        )}
      </div>
    </Modal>
  );
}
